import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ServerPagination from '../components/ServerPagination';
import Filters from '../components/Filters';
import AccordionList from '../components/AccordionList';
import ExportButtons from '../components/ExportButtons';
import PageSizeSelector from '../components/PageSizeSelector';
import { getBackgroundTaskLogsWithFilters, setFilters, resetFilters } from '../redux/backgroundTaskLogSlice';
import { fetchBackgroundTaskLogsWithFilters } from '../services/api';
import { handleExportResponse } from '../utils/exportUtils';
import RecordsLoader from '../components/RecordsLoader';
import { StatusBadge } from '../components/Badges';

const BackgroundTaskLogs = () => {
    const dispatch = useDispatch();
    const { logs, status, pagination, error } = useSelector((state) => state.backgroundTaskLogs);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [showFilters, setShowFilters] = useState(false);

    const [localFilters, setLocalFilters] = useState({
        task_name: '',
        task_type: '',
        task_id: '',
        related_id: '',
        related_type: '',
        log_level: '',
        admin_notification_sent: '',
        created_at_from: '',
        created_at_to: '',
    });

    // Filter configuration
    const filterConfig = [
        {
            key: 'task_name',
            label: 'Task Name',
            type: 'text',
            placeholder: 'Enter task name',
        },
        {
            key: 'task_type',
            label: 'Task Type',
            type: 'text',
            placeholder: 'Enter task type (e.g., payment_confirmation_emails)',
        },
        {
            key: 'task_id',
            label: 'Task ID',
            type: 'text',
            placeholder: 'Enter task ID',
        },
        {
            key: 'related_id',
            label: 'Related ID',
            type: 'number',
            placeholder: 'Enter related ID',
            min: 0,
        },
        {
            key: 'related_type',
            label: 'Related Type',
            type: 'text',
            placeholder: 'Enter related type (e.g., payment)',
        },
        {
            key: 'log_level',
            label: 'Log Level',
            type: 'select',
            options: [
                { value: 'SUCCESS', label: 'Success' },
                { value: 'ERROR', label: 'Error' },
                { value: 'WARNING', label: 'Warning' },
                { value: 'INFO', label: 'Info' },
            ],
        },
        {
            key: 'admin_notification_sent',
            label: 'Admin Notification Sent',
            type: 'select',
            options: [
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' },
            ],
        },
        {
            key: 'created_at_from',
            label: 'Created Date From',
            type: 'date',
        },
        {
            key: 'created_at_to',
            label: 'Created Date To',
            type: 'date',
        },
    ];

    // Fetch logs with current filters and pagination
    const fetchLogs = useCallback(() => {
        const params = {
            page: currentPage,
            page_size: pageSize,
            ...localFilters,
        };
        
        // Remove empty filter values
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });

        dispatch(getBackgroundTaskLogsWithFilters(params));
    }, [dispatch, currentPage, pageSize, localFilters]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleFilterChange = (filters) => {
        setLocalFilters(filters);
        dispatch(setFilters(filters));
        setCurrentPage(1);
    };

    const handleFilterReset = (filters) => {
        setLocalFilters(filters);
        dispatch(resetFilters());
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };

    const formatExecutionTime = (ms) => {
        if (!ms && ms !== 0) return '—';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const handleExport = async (format) => {
        try {
            const exportParams = { ...localFilters, export: format };
            
            Object.keys(exportParams).forEach(key => {
                if (exportParams[key] === '' || exportParams[key] === null || exportParams[key] === undefined) {
                    delete exportParams[key];
                }
            });

            const response = await fetchBackgroundTaskLogsWithFilters(exportParams);
            handleExportResponse(response, format, `background-task-logs.${format}`);
        } catch (error) {
            console.error('Export error:', error);
            alert(error.message || 'Failed to export background task logs. Please try again.');
        }
    };

    return (
        <div className="container mx-auto p-6">
            {status === 'loading' && <RecordsLoader text="Loading Background Task Logs, please hold on..." />}

            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl text-black font-bold">Background Task Logs</h1>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium flex items-center gap-2"
                >
                    <svg
                        className={`w-5 h-5 transition-transform duration-200 ${showFilters ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="mb-4">
                    <Filters
                        filters={filterConfig}
                        onFilterChange={handleFilterChange}
                        onReset={handleFilterReset}
                    />
                </div>
            )}

            {/* Export Buttons */}
            <div className="mb-4">
                <ExportButtons
                    onExport={handleExport}
                    isLoading={status === 'loading'}
                />
            </div>

            {/* Page Size Selector */}
            <div className="mb-4">
                <PageSizeSelector
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            {status === 'failed' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    Error loading Background Task Logs: {error || 'Unknown error'}
                </div>
            )}

            {/* Logs Accordion List */}
            {status === 'succeeded' && (
                <>
                    <AccordionList
                        data={logs || []}
                        getRowKey={(log) => log.id}
                        primaryFields={[
                            {
                                key: 'index',
                                label: '#',
                                render: (value, item, index) => {
                                    // Calculate descending index based on current page and page size
                                    const page = pagination?.page || currentPage;
                                    const pageSizeValue = pagination?.page_size || pageSize;
                                    const totalCount = pagination?.total_count || 0;
                                    const startIndex = (page - 1) * pageSizeValue;
                                    return totalCount - (startIndex + index);
                                },
                            },
                            {
                                key: 'task_name',
                                label: 'Task Name',
                                flex: true,
                            },
                            {
                                key: 'task_type',
                                label: 'Task Type',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'log_level',
                                label: 'Log Level',
                                render: (value) => <StatusBadge status={value} type={value === 'ERROR' ? 'error' : value === 'SUCCESS' ? 'success' : 'generic'} />,
                            },
                            {
                                key: 'admin_notification_sent',
                                label: 'Notification',
                                render: (value) => value ? 'Yes' : 'No',
                            },
                            {
                                key: 'created_at',
                                label: 'Created At',
                                render: (value) => formatDate(value),
                            },
                        ]}
                        expandedFields={[
                            {
                                key: 'task_id',
                                label: 'Task ID',
                                render: (value) => value || '—',
                                mono: true,
                            },
                            {
                                key: 'related_id',
                                label: 'Related ID',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'related_type',
                                label: 'Related Type',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'message',
                                label: 'Message',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'execution_time_ms',
                                label: 'Execution Time',
                                render: (value) => formatExecutionTime(value),
                            },
                            {
                                key: 'error_traceback',
                                label: 'Error Traceback',
                                render: (value) => {
                                    if (!value) return '—';
                                    return (
                                        <div className="bg-red-50 border border-red-200 rounded p-2 text-xs font-mono text-red-800 max-h-32 overflow-y-auto">
                                            {value}
                                        </div>
                                    );
                                },
                            },
                            {
                                key: 'details',
                                label: 'Details',
                                render: (value) => {
                                    if (!value) return '—';
                                    return (
                                        <pre className="bg-gray-50 border border-gray-200 rounded p-2 text-xs font-mono max-h-32 overflow-y-auto">
                                            {JSON.stringify(value, null, 2)}
                                        </pre>
                                    );
                                },
                            },
                            {
                                key: 'metadata',
                                label: 'Metadata',
                                render: (value) => {
                                    if (!value) return '—';
                                    return (
                                        <pre className="bg-gray-50 border border-gray-200 rounded p-2 text-xs font-mono max-h-32 overflow-y-auto">
                                            {JSON.stringify(value, null, 2)}
                                        </pre>
                                    );
                                },
                            },
                        ]}
                        emptyMessage="No background task logs found!"
                        allowMultipleExpand={true}
                    />

                    {/* Server-side Pagination */}
                    {pagination && pagination.total_pages > 0 && (
                        <ServerPagination
                            currentPage={pagination.page || currentPage}
                            totalPages={pagination.total_pages || 1}
                            onPageChange={handlePageChange}
                            totalCount={pagination.total_count || 0}
                            pageSize={pagination.page_size || pageSize}
                            showPageInfo={true}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default BackgroundTaskLogs;
